import React, { useEffect, useState, useCallback } from "react";
import { api, getErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Users, Building2, RefreshCw, Search, X, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "entreprises", label: "Entreprises", icon: Building2, testid: "tab-bedee-entreprises" },
  { key: "contacts", label: "Contacts", icon: Users, testid: "tab-bedee-contacts" },
];

const PAGE_SIZE = 50;

function EmptyState({ icon: Icon, message }) {
  return (
    <tr>
      <td colSpan={10} className="py-16 text-center">
        <Icon size={28} strokeWidth={1} className="text-zinc-300 mx-auto mb-3" />
        <div className="text-sm text-zinc-400 font-mono">{message}</div>
      </td>
    </tr>
  );
}

export default function Contacts() {
  const [tab, setTab] = useState("entreprises");
  const [contacts, setContacts] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [totals, setTotals] = useState({ contacts: null, entreprises: null });
  const [hasMore, setHasMore] = useState({ contacts: false, entreprises: false });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [q, setQ] = useState("");
  const [pollInterval, setPollInterval] = useState(null);

  // Charger les totaux depuis les stats Bedee au montage
  const loadStats = useCallback(async () => {
    try {
      const r = await api.get("/integrations/bedee/stats");
      setTotals({ contacts: r.data.nb_contacts, entreprises: r.data.nb_entreprises });
    } catch {}
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const loadContacts = useCallback(async (search = "", p = 1, append = false) => {
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const params = { page: p, page_size: PAGE_SIZE };
      if (search) params.q = search;
      const r = await api.get("/contacts/bedee-contacts-list", { params });
      setContacts(prev => append ? [...prev, ...r.data.items] : r.data.items);
      setHasMore(prev => ({ ...prev, contacts: r.data.has_more }));
      setTotals(prev => ({ ...prev, contacts: r.data.total }));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, []);

  const loadEntreprises = useCallback(async (search = "", p = 1, append = false) => {
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const params = { page: p, page_size: PAGE_SIZE };
      if (search) params.q = search;
      const r = await api.get("/contacts/bedee-entreprises-list", { params });
      setEntreprises(prev => append ? [...prev, ...r.data.items] : r.data.items);
      setHasMore(prev => ({ ...prev, entreprises: r.data.has_more }));
      setTotals(prev => ({ ...prev, entreprises: r.data.total }));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, []);

  // Rechargement initial quand l'onglet ou la recherche change
  useEffect(() => {
    setPage(1);
    if (tab === "contacts") loadContacts(q, 1, false);
    else loadEntreprises(q, 1, false);
  }, [tab, q, loadContacts, loadEntreprises]);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    if (tab === "contacts") loadContacts(q, nextPage, true);
    else loadEntreprises(q, nextPage, true);
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/integrations/bedee/sync", { force_full: false });
      toast.success(r.data.message || "Synchronisation lancée en arrière-plan.");
      let count = 0;
      const interval = setInterval(async () => {
        count++;
        setPage(1);
        if (tab === "contacts") await loadContacts(q, 1, false);
        else await loadEntreprises(q, 1, false);
        await loadStats();
        if (count >= 30) { clearInterval(interval); setPollInterval(null); setSyncing(false); }
      }, 10000);
      setPollInterval(interval);
    } catch (err) {
      toast.error(getErrorMessage(err));
      setSyncing(false);
    }
  };

  useEffect(() => {
    return () => { if (pollInterval) clearInterval(pollInterval); };
  }, [pollInterval]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit", month: "short", year: "numeric",
      }).format(new Date(iso));
    } catch { return iso; }
  };

  const currentHasMore = tab === "contacts" ? hasMore.contacts : hasMore.entreprises;

  return (
    <div className="min-h-screen">
      <PageHeader
        title="Contacts"
        subtitle="Annuaire Bedee CRM — entreprises et contacts synchronisés"
        index="05"
      >
        <button
          data-testid="bedee-sync-contacts-btn"
          onClick={sync}
          disabled={syncing}
          className="inline-flex items-center gap-2 border border-zinc-900 text-zinc-900 hover:bg-zinc-100 text-sm font-medium px-4 py-2 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={14} strokeWidth={1.5} className={syncing ? "animate-spin" : ""} />
          {syncing ? "Synchronisation…" : "Synchroniser Bedee"}
        </button>
      </PageHeader>

      {/* Tabs */}
      <div className="border-b border-zinc-200 bg-white sticky top-[89px] z-[5]">
        <div className="px-6 md:px-10 flex">
          {TABS.map((t) => {
            const isActive = tab === t.key;
            const count = totals[t.key];
            return (
              <button
                key={t.key}
                data-testid={t.testid}
                onClick={() => { setTab(t.key); setQ(""); }}
                className={`relative inline-flex items-center gap-2 px-4 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive ? "text-[#002FA7]" : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                <t.icon size={14} strokeWidth={1.5} />
                {t.label}
                <span className="ml-1 text-[10px] font-mono text-zinc-400">
                  {count !== null ? count.toLocaleString("fr-FR") : "…"}
                </span>
                {isActive && (
                  <span className="absolute left-0 right-0 bottom-[-1px] h-[2px] bg-[#002FA7]" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-6 md:p-10 space-y-4">
        {/* Barre de recherche */}
        <div className="relative max-w-sm">
          <Search size={13} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <input
            data-testid="contacts-search"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tab === "contacts" ? "Rechercher un contact…" : "Rechercher une entreprise…"}
            className="w-full border border-zinc-200 pl-8 pr-8 py-2 text-sm font-mono bg-white focus:outline-none focus:border-zinc-500"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
            >
              <X size={12} strokeWidth={1.5} />
            </button>
          )}
        </div>

        {/* Table contacts */}
        {tab === "contacts" && (
          <div className="border border-zinc-200 bg-white" data-testid="bedee-contacts-table">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50/50">
                  <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Prénom</th>
                  <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Nom</th>
                  <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Email</th>
                  <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Entreprise</th>
                  <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Màj Bedee</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="p-6 text-sm text-zinc-400 font-mono">Chargement…</td></tr>
                ) : contacts.length === 0 ? (
                  <EmptyState
                    icon={Users}
                    message={q ? `Aucun contact pour « ${q} »` : "Aucun contact. Cliquez sur Synchroniser Bedee."}
                  />
                ) : contacts.map((c) => (
                  <tr key={c.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/60 transition-colors">
                    <td className="py-3 px-4 text-sm text-zinc-600">{c.prenom || "—"}</td>
                    <td className="py-3 px-4 text-sm font-medium text-zinc-900">{c.nom || "—"}</td>
                    <td className="py-3 px-4 text-sm text-zinc-500">
                      {c.email
                        ? <a href={`mailto:${c.email}`} className="font-mono text-[12px] text-[#002FA7] hover:underline">{c.email}</a>
                        : <span className="text-zinc-300">—</span>
                      }
                    </td>
                    <td className="py-3 px-4 text-sm text-zinc-500">
                      {c.entreprise_nom ? (
                        c.entreprise_nom.startsWith("ENT-") ? (
                          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono text-zinc-400 border border-zinc-200 bg-zinc-50 px-1.5 py-0.5">
                            {c.entreprise_nom}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5">
                            <Building2 size={11} strokeWidth={1.5} className="text-zinc-400 shrink-0" />
                            {c.entreprise_nom}
                          </span>
                        )
                      ) : "—"}
                    </td>
                    <td className="py-3 px-4 text-[11px] font-mono text-zinc-400">
                      {formatDate(c.bedee_updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {currentHasMore && tab === "contacts" && (
              <div className="border-t border-zinc-100 p-3 flex justify-center">
                <button
                  data-testid="contacts-load-more"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 text-xs font-mono text-zinc-500 hover:text-zinc-900 transition-colors disabled:opacity-40"
                >
                  <ChevronDown size={13} strokeWidth={1.5} />
                  {loadingMore ? "Chargement…" : `Charger plus (${contacts.length} / ${totals.contacts?.toLocaleString("fr-FR")})`}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Table entreprises */}
        {tab === "entreprises" && (
          <div className="border border-zinc-200 bg-white" data-testid="bedee-entreprises-table">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50/50">
                  <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Nom</th>
                  <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Identifiant Bedee</th>
                  <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Màj Bedee</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3} className="p-6 text-sm text-zinc-400 font-mono">Chargement…</td></tr>
                ) : entreprises.length === 0 ? (
                  <EmptyState
                    icon={Building2}
                    message={q ? `Aucune entreprise pour « ${q} »` : "Aucune entreprise. Cliquez sur Synchroniser Bedee."}
                  />
                ) : entreprises.map((e) => (
                  <tr key={e.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/60 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-zinc-900">{e.nom}</td>
                    <td className="py-3 px-4 text-[11px] font-mono text-zinc-400">{e.id}</td>
                    <td className="py-3 px-4 text-[11px] font-mono text-zinc-400">
                      {formatDate(e.bedee_updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {currentHasMore && tab === "entreprises" && (
              <div className="border-t border-zinc-100 p-3 flex justify-center">
                <button
                  data-testid="entreprises-load-more"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 text-xs font-mono text-zinc-500 hover:text-zinc-900 transition-colors disabled:opacity-40"
                >
                  <ChevronDown size={13} strokeWidth={1.5} />
                  {loadingMore ? "Chargement…" : `Charger plus (${entreprises.length} / ${totals.entreprises?.toLocaleString("fr-FR")})`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
